import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { EventCategoryDto } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import type { TabItem } from "@/design-system/Tabs";
import { parseQueryParamToBoolean } from "@/helpers/query-params";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useQueryParams } from "@/hooks/useQueryParam";
import type { CalendarTab } from "@/modules/calendar/constants";
import { EVENTS_TAB, RESERVATIONS_TAB } from "@/modules/calendar/constants";
import { useBookableAssetsQueries } from "@/queries/bookable-assets/queryOptions";
import { useEventQueries } from "@/queries/events/queryOptions";
import { useTranslation } from "@/translations";
import type { ApiQueryParams } from "@/types/api-types";

import type { CalendarRouteParams } from "../../routes";
import { CalendarOverviewContent, type CalendarOverviewContentProps } from "./CalendarOverviewContent";
import type { EventFilterValues } from "./components/EventFilterDrawer";

const CALENDAR_TABS: CalendarTab[] = [EVENTS_TAB, RESERVATIONS_TAB];

const EVENT_TYPES: EventCategoryDto["id"][] = ["unknown", "social", "sport", "maintenance"];

export type EventsFilters = Omit<ApiQueryParams<"getEventsV1">, "Offset" | "Limit">;

export function CalendarOverviewPage(): React.ReactNode {
  const [reservationsSearch, setReservationsSearch] = useState("");
  const { i18n, t } = useTranslation();
  const [queryParams, setQueryParams] = useQueryParams<CalendarRouteParams["list"]>();
  const [search, setSearch] = useState(() => queryParams.search ?? "");

  const activeTab = queryParams.tab && CALENDAR_TABS.includes(queryParams.tab) ? queryParams.tab : CALENDAR_TABS[0];
  const debouncedQuery = useDebounceValue(search.toLowerCase().trim(), 300);

  useEffect(() => {
    setQueryParams({ search: debouncedQuery || undefined }, { replace: true });
  }, [debouncedQuery, setQueryParams]);
  const currFilter: EventFilterValues = {
    createdBySelf: parseQueryParamToBoolean(queryParams.createdBySelf),
    signedUpBySelf: parseQueryParamToBoolean(queryParams.signedUpBySelf),
    category: EVENT_TYPES.includes(queryParams.category as any)
      ? (queryParams.category as EventCategoryDto["id"])
      : undefined,
  };

  const upcomingEventsQuery: EventsFilters = {
    Category: currFilter.category,
    CreatedByMe: currFilter.createdBySelf,
    IsSignedUp: currFilter.signedUpBySelf,
    Search: debouncedQuery,
    TimeFrame: "future",
    Connection: "included",
  };
  const pastEventsQuery: EventsFilters = {
    ...upcomingEventsQuery,
    TimeFrame: "past",
  };

  const eventQueries = useEventQueries();
  const { isPending: isPendingEventCategories, error: eventCategoriesError } = useQuery(eventQueries.listCategories());
  const {
    data: upcomingEventsData,
    error: upcomingEventsError,
    isPending: isPendingUpcomingEvents,
    hasNextPage: hasMoreUpcomingEvents,
    fetchNextPage: fetchMoreUpcomingEvents,
    isFetchingNextPage: isFetchingMoreUpcomingEvents,
  } = useInfiniteQuery(eventQueries.listInfinite(upcomingEventsQuery));
  const {
    data: pastEventsData,
    error: pastEventsError,
    isPending: isPendingPastEvents,
    hasNextPage: hasMorePastEvents,
    fetchNextPage: fetchMorePastEvents,
    isFetchingNextPage: isFetchingMorePastEvents,
  } = useInfiniteQuery(eventQueries.listInfinite(pastEventsQuery));

  const bookableAssetsQueries = useBookableAssetsQueries();
  const {
    data: upcomingReservationsData,
    hasNextPage: hasMoreUpcomingReservations,
    fetchNextPage: fetchMoreUpcomingReservations,
    isFetchingNextPage: isLoadingMoreUpcomingReservations,
    isPending: isPendingUpcomingReservations,
    error: upcomingReservationsError,
  } = useInfiniteQuery(
    bookableAssetsQueries.listMyBookingsInfinite({
      query: {
        TimeFrame: "futureOrToday",
      },
    }),
  );
  const {
    data: pastReservationsData,
    hasNextPage: hasMorePastReservations,
    fetchNextPage: fetchMorePastReservations,
    isFetchingNextPage: isLoadingMorePastReservations,
    isPending: isPendingPastReservations,
    error: pastReservationsError,
  } = useInfiniteQuery(
    bookableAssetsQueries.listMyBookingsInfinite({
      query: {
        TimeFrame: "past",
      },
    }),
  );

  const handleUpdateFilter = useCallback(
    (filter: EventFilterValues) => {
      setQueryParams({
        category: filter.category,
        createdBySelf: filter.createdBySelf ? "true" : undefined,
        signedUpBySelf: filter.signedUpBySelf ? "true" : undefined,
      });
    },
    [setQueryParams],
  );

  const totalUpcomingEvents = upcomingEventsData?.pages[0].total ?? 0;
  const totalPastEvents = pastEventsData?.pages[0].total ?? 0;
  const futureEvents = upcomingEventsData?.pages.flatMap((x) => x.items) ?? [];
  const pastEvents = pastEventsData?.pages.flatMap((x) => x.items) ?? [];
  const totalUpcomingReservations = upcomingReservationsData?.pages[0].total ?? 0;
  const totalPastReservations = pastReservationsData?.pages[0].total ?? 0;
  const upcomingReservations = useMemo(
    () => upcomingReservationsData?.pages.flatMap((x) => x.items) ?? [],
    [upcomingReservationsData],
  );
  const pastReservations = useMemo(
    () => pastReservationsData?.pages.flatMap((x) => x.items) ?? [],
    [pastReservationsData],
  );
  const mappedUpcomingReservations = useMemo(
    () =>
      upcomingReservations.map((reservation) => ({
        ...reservation,
        searchable:
          `${reservation.asset.name}|||${formatDate(i18n, "month", parseISO(reservation.date))}|||${reservation.date}|||${reservation.startTime}|||${reservation.endTime}|||${reservation.asset.locationSpecification ?? ""}`.toLowerCase(),
      })),
    [upcomingReservations, i18n],
  );
  const mappedPastReservations = useMemo(
    () =>
      pastReservations.map((reservation) => ({
        ...reservation,
        searchable:
          `${reservation.asset.name}|||${formatDate(i18n, "month", parseISO(reservation.date))}|||${reservation.date}|||${reservation.startTime}|||${reservation.endTime}|||${reservation.asset.locationSpecification ?? ""}`.toLowerCase(),
      })),
    [pastReservations, i18n],
  );
  const filteredUpcomingReservations = useMemo(
    () => mappedUpcomingReservations.filter((reservation) => reservation.searchable.includes(reservationsSearch)),
    [reservationsSearch, mappedUpcomingReservations],
  );
  const filteredPastReservations = useMemo(
    () => mappedPastReservations.filter((reservation) => reservation.searchable.includes(reservationsSearch)),
    [reservationsSearch, mappedPastReservations],
  );

  const tabs: TabItem<CalendarTab>[] = [
    {
      label: t("page.calendar.tabs.events"),
      value: EVENTS_TAB,
      count: totalUpcomingEvents,
    },
  ];

  if (upcomingReservations.length !== 0 || pastReservations.length !== 0) {
    tabs.push({
      label: t("page.calendar.tabs.reservations"),
      value: RESERVATIONS_TAB,
      count: upcomingReservations.filter((reservation) => !reservation.cancelledAt).length,
    });
  }

  const error =
    eventCategoriesError ||
    upcomingEventsError ||
    pastEventsError ||
    upcomingReservationsError ||
    pastReservationsError;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isPendingEventCategories;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: CalendarOverviewContentProps = {
    search,
    onChangeSearch: setSearch,
    activeTab: activeTab,
    onChangeTab: (newTab) => setQueryParams({ tab: newTab }),
    tabs,
    eventFilter: currFilter,
    onUpdateEventFilter: handleUpdateFilter,
    events: {
      upcoming: {
        items: futureEvents,
        total: totalUpcomingEvents,
        isLoading: isPendingUpcomingEvents,
        loadMore: fetchMoreUpcomingEvents,
        isLoadingMore: isFetchingMoreUpcomingEvents,
        hasMore: hasMoreUpcomingEvents,
      },
      past: {
        items: pastEvents,
        hasMore: hasMorePastEvents,
        isLoading: isPendingPastEvents,
        loadMore: fetchMorePastEvents,
        isLoadingMore: isFetchingMorePastEvents,
        total: totalPastEvents,
      },
    },
    reservationsSearch,
    onReservationsSearch: setReservationsSearch,
    reservations: {
      upcoming: {
        items: filteredUpcomingReservations,
        hasMore: hasMoreUpcomingReservations,
        isLoading: isPendingUpcomingReservations,
        loadMore: fetchMoreUpcomingReservations,
        isLoadingMore: isLoadingMoreUpcomingReservations,
        total: totalUpcomingReservations,
      },
      past: {
        items: filteredPastReservations,
        hasMore: hasMorePastReservations,
        isLoading: isPendingPastReservations,
        loadMore: fetchMorePastReservations,
        isLoadingMore: isLoadingMorePastReservations,
        total: totalPastReservations,
      },
    },
  };

  return <CalendarOverviewContent {...contentProps} />;
}
